import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { OENActivationExpiryMode } from "../../order-entry/data/enums/oe-activation-expiry-mode.enum";
import { OENLockedState } from "../../order-entry/data/enums/oe-locked-state.enum";
import { ICNInputControlState } from "../../order-entry/interface/controls/models/bfx-interface-controls.enums";
import {
  SECS_MASK,
  SECS_DEFAULT,
  SECS_VALIDATE_TIME_PATTERN,
  NOSECS_MASK,
  NOSECS_DEFAULT,
  NOSECS_VALIDATE_TIME_PATTERN,
} from "../../order-entry/interface/controls/bfx-time-input.component";
import {
  ICAutocompleteGenericModel,
  ICFixedCcyModel,
} from "app/order-entry/interface/controls/models/bfx-interface-controls.models";

export type GoodFieldType = {
  label: string;
  labelKey: string;
  tooltip: string;
  tooltipKey: string;
  value: ICAutocompleteGenericModel;
  suggestions: Array<ICAutocompleteGenericModel>;
  validDataset: Array<ICAutocompleteGenericModel>;
  allDataset: Array<ICAutocompleteGenericModel>;
};

export class OEDefaultsModel {
  public client: {
    value: CubiqTS.ClientImpl;
    accountConstraint: CubiqTS.AccountImpl;
    disabled: boolean;
  } = {
    value: null, // ClientImpl
    accountConstraint: null,
    disabled: false,
  };

  public account: {
    value: CubiqTS.AccountImpl; // AccountImpl
    disabled: boolean;
  } = {
    value: null, // AccountImpl
    disabled: false,
  };

  public currencyPair: {
    label: string;
    labelKey: string;
    dataset: Array<any>;
    product: string; // Product code (base + nonBase)
    base: string;
    nonBase: string;
    model: CubiqTS.CurrencyPairImpl;
    inverted: boolean; // Has the inverted pair been selected
    fwdEnabled: boolean;
    ndfEnabled: boolean;
    mktEnabled: boolean;
    tslEnabled: boolean;
  } = {
    label: "Pair",
    labelKey: "order_entry.panel.label.ccy_pair",
    dataset: [],
    product: null, // Product code (base + nonBase)
    base: "",
    nonBase: "",
    model: null,
    inverted: false, // Has the inverted pair been selected
    fwdEnabled: false,
    ndfEnabled: false,
    mktEnabled: true,
    tslEnabled: false,
  };

  public algoType: any = {
    label: "Type",
    labelKey: "order_entry.panel.label.order_type",
    dataset: [],
    disabled: false,
    value: null, // BfxAutocompleteGenericModel
  };

  public orderType: any = {
    label: "Type",
    labelKey: "order_entry.panel.label.order_type",
    dataset: [],
    fullDataset: [],
    disabled: false,
    value: null, // BfxAutocompleteGenericModel
  };

  public clientIntent: any = {
    label: "Client intent",
    labelKey: "order_entry.panel.label.client_intent",
    value: CubiqTS.OrderIntent.BUY, // type: CubiqTS.OrderIntent
    intent: false, // buy = false
  };

  public fixedCcy: {
    value: ICFixedCcyModel;
    fixedCcy: string; // string (3)
    ccyPair: string; // string (6)
  } = {
    value: null,
    fixedCcy: null, // string (3)
    ccyPair: null, // string (6)
  };

  public fixingSource: any = {
    label: "Fixing Code",
    labelKey: "order_entry.panel.label.fixing_source",
    value: null,
    dataset: [],
    suggestions: [],
    disabled: true,
    customFixingAllowed: true,
  };

  public fixing: any = {
    label: "Fixing Time",
    labelKey: "order_entry.panel.label.fixing_time",
    value: null,
    dataset: [],
    suggestions: [],
    disabled: true,
  };

  public valueDate: any = {
    label: "Value Date",
    dataset: [],
    suggestions: [],
    disabled: true,
    value: null, // BfxAutocompleteGenericModel
  };

  public goodFrom: GoodFieldType = {
    label: "G.F.",
    labelKey: "order_entry.good.from",
    tooltip: "Good From",
    tooltipKey: "order_entry.good.from.tooltip",
    value: null,
    suggestions: [],
    validDataset: [],
    allDataset: [],
  };

  public goodUntil: GoodFieldType = {
    label: "T",
    labelKey: "order_entry.good.until",
    tooltip: "Good Until",
    tooltipKey: "order_entry.good.until.tooltip",
    value: null,
    suggestions: [],
    validDataset: [],
    allDataset: [],
  };

  public orderAmount: any = {
    label: "Amount",
    labelKey: "order_entry.panel.label.order_amount",
    value: null, // type: BigNumber
    max: new CubiqTS.BigNumber("999999999999999.99"),
  };

  public privateOrder: any = {
    // Checkbox
    label: "Private Order",
    labelKey: "order_entry.privateOrder",
    show: false,
    value: false,
  };

  public delta: {
    label: string;
    labelKey: string;
    value: number;
    oldValue: number;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
    precision: number;
    step: number;
    disabled: boolean;
    minValue: number;
  } = {
    label: "Delta",
    labelKey: "order_entry.panel.label.delta",
    value: null,
    oldValue: null,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
    precision: 1,
    step: 1,
    disabled: false,
    minValue: 0,
  };

  public rungsAbove: {
    label: string;
    labelKey: string;
    value: number;
    oldValue: number;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
    disabled: boolean;
    maxRungs: number;
    max: number;
  } = {
    label: "Rungs Above",
    labelKey: "order_entry.panel.label.rungsAbove",
    value: null,
    oldValue: null,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
    disabled: false,
    maxRungs: 20,
    max: 20,
  };

  public rungsBelow: {
    label: string;
    labelKey: string;
    value: number;
    oldValue: number;
    externalState: ICNInputControlState;
    lockState: OENLockedState;
    disabled: boolean;
    max: number;
  } = {
    label: "Rungs Below",
    labelKey: "order_entry.panel.label.rungsBelow",
    value: null,
    oldValue: null,
    externalState: null,
    lockState: OENLockedState.UNLOCKED,
    disabled: false,
    max: 20,
  };
}
